class ProductRegistrationForm {
  constructor(options) {
    this.options = options;
    this.form = document.getElementById('product-info-form');

    // Enhanced ATC button detection with fallbacks (TASK-004)
    this.productFormContainer = document.querySelector('.product-form');
    this.addToCartButton = this._findAddToCartButton();
    this.variantSelect = this.productFormContainer?.querySelector('[name="id"]');

    // Schedule elements (may not exist if no schedule)
    this.dateSelect = document.getElementById('date-select');
    this.monthSelect = document.getElementById('month-select');
    this.weekSelect = document.getElementById('week-select');
    this.daySelect = document.getElementById('day-select');
    this.timeSelect = document.getElementById('time-select');
    this.scheduleSummary = document.getElementById('schedule-summary');
    this.registrationTypeRadios = this.form?.querySelectorAll('input[name="registration_type"]');

    // Student elements
    this.studentsContainer = document.getElementById('students-container');
    this.addStudentButton = document.getElementById('add-student');
    this.removeStudentButton = document.getElementById('remove-student');

    // Status elements
    this.formStatus = document.getElementById('form-status');
    this.generalErrorMessage = this.formStatus?.querySelector('.general-error-message');
    this.generalSuccessMessage = this.formStatus?.querySelector('.general-success-message');

    // State
    this.isSubmitting = false;
    this.studentCount = 1;
    this.maxStudents = 5; // Or get from settings/metafield if dynamic
    this.scheduleData = null;
    this.currentVariantId = this.options.initialVariantId;
    this.variantAvailable = this.options.variantAvailable ?? true;
    this.variantsMap = this._buildVariantsMap(this.options.variantsJson);

    // Age limits from options (TASK-007)
    this.minAge = this.options.minAge ?? 7;
    this.maxAge = this.options.maxAge ?? 18;

    if (!this.form || !this.addToCartButton) {
      console.warn('ProductRegistrationForm: Required elements not found.', {
        form: !!this.form,
        atcButton: !!this.addToCartButton,
        productFormContainer: !!this.productFormContainer
      });
      return;
    }

    this._init();
  }

  // TASK-004: Enhanced ATC button detection with fallback selectors
  _findAddToCartButton() {
    const selectors = [
      '.product-form [name="add"]',
      '.product-form button[type="submit"]',
      '.product-form__submit',
      'button[name="add"]',
      '[data-add-to-cart]',
      '#AddToCart',
      '.add-to-cart',
      'form[action*="/cart/add"] button[type="submit"]'
    ];

    for (const selector of selectors) {
      const button = document.querySelector(selector);
      if (button) return button;
    }

    return null;
  }

  _init() {
    this._parseScheduleData();
    this._bindEvents();
    this._restoreFormData();
    this._updateScheduleSummary(); // Initial update
    this._toggleDateSelectionVisibility(); // Initial state for date selector
    this._updateAvailabilityUI(); // Initial availability state
  }

  _buildVariantsMap(variants) {
    const map = new Map();
    if (Array.isArray(variants)) {
      variants.forEach(variant => {
        map.set(String(variant.id), variant.available);
      });
    }
    return map;
  }

  _updateVariantAvailability(variantId) {
    const variantIdStr = String(variantId);
    if (this.variantsMap.has(variantIdStr)) {
      this.variantAvailable = this.variantsMap.get(variantIdStr);
    } else {
      // Fallback: assume available if not in map
      this.variantAvailable = true;
    }
    this._updateAvailabilityUI();
  }

  _updateAvailabilityUI() {
    // Clear any previous availability errors when variant becomes available
    if (this.variantAvailable) {
      this._clearAllErrors();
    }
  }

  _parseScheduleData() {
    const scheduleScript = document.getElementById('schedule-data');
    if (scheduleScript) {
      try {
        this.scheduleData = JSON.parse(scheduleScript.textContent.trim());
      } catch (error) {
        console.error('Error parsing schedule data:', error, scheduleScript.textContent);
        this.scheduleData = null; // Ensure it's null on error
      }
    }
  }

  _bindEvents() {
    // Use event delegation for input changes within the form for efficiency
    this.form.addEventListener('change', (e) => {
      const target = e.target;
      if (target.matches('select, input')) {
        this._handleInputChange(target);
        this._saveFormData(); // Save on any change
      }
    });

    // Handle clicks on specific buttons
    this.addToCartButton.addEventListener('click', (e) => this._handleSubmit(e));
    this.addStudentButton?.addEventListener('click', () => this._addStudent());
    this.removeStudentButton?.addEventListener('click', () => this._removeStudent());

    // Listen for variant changes on the standard Shopify form
    if (this.variantSelect) {
      this.variantSelect.addEventListener('change', (e) => {
        this.currentVariantId = e.target.value;
        this._updateVariantAvailability(this.currentVariantId);
        console.log('Variant changed to:', this.currentVariantId, 'Available:', this.variantAvailable);
      });
    }

    // Also listen for custom variant change events (used by some themes)
    document.addEventListener('variant:change', (e) => {
      if (e.detail?.variant) {
        this.currentVariantId = e.detail.variant.id;
        this.variantAvailable = e.detail.variant.available;
        this._updateAvailabilityUI();
      }
    });
  }

  _handleInputChange(target) {
    // Clear validation message on input change
    this._clearFieldError(target);

    if (target === this.daySelect) {
      this._populateTimeSlots();
    }
    if (target === this.dateSelect || target === this.monthSelect || target === this.weekSelect || target === this.daySelect || target === this.timeSelect) {
      this._updateScheduleSummary();
    }
    if (target.matches('input[name="registration_type"]')) {
      this._toggleDateSelectionVisibility();
      this._updateScheduleSummary(); // Update summary when switching modes
    }
  }

  _populateTimeSlots() {
    const selectedDay = this.daySelect?.value;
    if (!this.timeSelect || !this.scheduleData?.schedule) {
      if (this.timeSelect) this.timeSelect.disabled = true;
      return;
    }

    this.timeSelect.innerHTML = '<option value="" disabled selected>-- Choose a time --</option>'; // Reset
    this.timeSelect.disabled = !selectedDay;

    if (selectedDay) {
      const daySchedule = this.scheduleData.schedule[selectedDay];
      const timeSlots = Array.isArray(daySchedule) ? daySchedule : []; // Ensure it's an array

      if (timeSlots.length > 0) {
        timeSlots.forEach(slot => {
          if (slot && slot.startTime && slot.endTime) {
            const option = document.createElement('option');
            const value = `${slot.startTime}-${slot.endTime}`;
            option.value = value;
            option.textContent = this._formatTimeSlot(slot.startTime, slot.endTime);
            this.timeSelect.appendChild(option);
          }
        });
        this.timeSelect.disabled = false;
      } else {
        this.timeSelect.disabled = true; // Disable if no slots for the selected day
      }
    }
  }

  _formatTimeSlot(startTime, endTime) {
    const format = (time) => {
      if (!time || !time.includes(':')) return ''; // Basic validation
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours, 10);
      const minute = parseInt(minutes, 10);
      if (isNaN(hour) || isNaN(minute)) return ''; // More validation

      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12; // Convert 0 to 12 for 12 AM/PM
      const minutePadded = minute.toString().padStart(2, '0');
      return `${hour12}:${minutePadded} ${ampm}`;
    };
    return `${format(startTime)} - ${format(endTime)}`;
  }

  _updateScheduleSummary() {
    if (!this.scheduleSummary) return;

    let summary = "";
    const registrationType = this.form.querySelector('input[name="registration_type"]:checked')?.value;

    if (this.monthSelect?.value) {
      summary += `Month: ${this.monthSelect.options[this.monthSelect.selectedIndex].text}. `;
    }
    if (this.weekSelect?.value) {
      summary += `Week: ${this.weekSelect.options[this.weekSelect.selectedIndex].text}. `;
    }
    if (this.daySelect?.value && this.timeSelect?.value) {
      summary += `Weekly on ${this.daySelect.options[this.daySelect.selectedIndex].text} at ${this.timeSelect.options[this.timeSelect.selectedIndex].text}. `;
    }
    if (this.options.hasDateSelection) {
      if (registrationType === 'single' && this.dateSelect?.value) {
        const selectedDateText = this.dateSelect.options[this.dateSelect.selectedIndex].text;
        summary += `Date: ${selectedDateText}.`;
      } else if (registrationType === 'all') {
        const dateCount = this.dateSelect ? this.dateSelect.querySelectorAll('option[value]:not([value=""])').length : 0;
        summary += `Registering for all ${dateCount} available dates.`;
      }
    }

    this.scheduleSummary.textContent = summary.trim() ? `Selected Schedule: ${summary}` : '';
    this.scheduleSummary.classList.toggle('hidden', !summary.trim());
  }

  _toggleDateSelectionVisibility() {
    if (!this.options.hasDateSelection || !this.dateSelect) return;

    const registrationType = this.form.querySelector('input[name="registration_type"]:checked')?.value;
    const dateSelectorContainer = this.dateSelect.closest('.date-selector');

    if (registrationType === 'single') {
      dateSelectorContainer?.classList.remove('hidden');
      this.dateSelect.required = true;
      this.dateSelect.disabled = false;
    } else {
      dateSelectorContainer?.classList.add('hidden');
      this.dateSelect.required = false;
      this.dateSelect.disabled = true;
      this.dateSelect.value = ''; // Clear selection when hiding
      this._clearFieldError(this.dateSelect); // Clear validation error if any
    }
  }

  _addStudent() {
    if (this.studentCount >= this.maxStudents) {
      this._showGeneralError(`Maximum of ${this.maxStudents} students allowed.`);
      return;
    }

    const template = this.studentsContainer.querySelector('.student-section[data-student-index="0"]');
    if (!template) return; // Should not happen

    const newStudentSection = template.cloneNode(true);
    const newIndex = this.studentCount;
    newStudentSection.dataset.studentIndex = newIndex;
    newStudentSection.querySelector('h4').textContent = `Student ${newIndex + 1}`;

    // Update IDs and names for the new section
    newStudentSection.querySelectorAll('label, input').forEach(el => {
      const id = el.getAttribute('id');
      const name = el.getAttribute('name');
      const htmlFor = el.getAttribute('for');

      if (id) el.id = id.replace('-0', `-${newIndex}`);
      if (name) el.name = name.replace('-0', `-${newIndex}`);
      if (htmlFor) el.htmlFor = htmlFor.replace('-0', `-${newIndex}`);

      // Clear input values and validation states in the clone
      if (el.tagName === 'INPUT') {
        el.value = '';
        el.classList.remove('is-invalid');
      }
      // Clear error message divs
      const errorDiv = el.parentElement?.querySelector('.field-error-message');
      if (errorDiv) errorDiv.textContent = '';
    });

    this.studentsContainer.appendChild(newStudentSection);
    this.studentCount++;
    this.removeStudentButton?.classList.remove('hidden');

    if (this.studentCount >= this.maxStudents) {
      this.addStudentButton.disabled = true;
    }
    this._saveFormData(); // Save state after adding
  }

  _removeStudent() {
    if (this.studentCount <= 1) return;

    const lastStudent = this.studentsContainer.querySelector(`.student-section[data-student-index="${this.studentCount - 1}"]`);
    lastStudent?.remove();
    this.studentCount--;

    if (this.studentCount === 1) {
      this.removeStudentButton?.classList.add('hidden');
    }

    this.addStudentButton.disabled = false;
    this._saveFormData(); // Save state after removing
  }

  _validateForm() {
    let isValid = true;
    this._clearAllErrors(); // Clear previous errors

    // Select only fields that are currently relevant and visible
    const fieldsToValidate = Array.from(this.form.querySelectorAll('input[required], select[required]'))
      .filter(field => {
        // Check if field itself or any ancestor is hidden
        return !field.closest('.hidden') && field.offsetParent !== null && !field.disabled;
      });

    fieldsToValidate.forEach(field => {
      let fieldValid = true;
      const value = field.value.trim();

      if (!value) {
        fieldValid = false;
        this._showFieldError(field, 'This field is required.');
      } else if (field.type === 'email' && field.pattern && !new RegExp(field.pattern).test(value)) {
        fieldValid = false;
        this._showFieldError(field, field.title || 'Please enter a valid email address.');
      } else if (field.type === 'tel' && field.pattern && !new RegExp(field.pattern).test(value)) {
        fieldValid = false;
        this._showFieldError(field, field.title || 'Please enter a valid phone number.');
      } else if (field.type === 'number') {
        const numValue = parseFloat(value);
        const min = parseFloat(field.min);
        const max = parseFloat(field.max);
        // TASK-007: Use configurable age limits for age fields
        const isAgeField = field.name && field.name.includes('student-age');
        const effectiveMin = isAgeField ? this.minAge : min;
        const effectiveMax = isAgeField ? this.maxAge : max;

        if (!isNaN(effectiveMin) && numValue < effectiveMin) {
          fieldValid = false;
          this._showFieldError(field, `Age must be ${effectiveMin} or older.`);
        } else if (!isNaN(effectiveMax) && numValue > effectiveMax) {
          fieldValid = false;
          this._showFieldError(field, `Age must be ${effectiveMax} or younger.`);
        }
      }

      if (!fieldValid) {
        isValid = false;
        field.classList.add('is-invalid');
      } else {
        field.classList.remove('is-invalid');
      }
    });

    if (!isValid) {
      this._showGeneralError('Please correct the errors above.');
      // Focus the first invalid field
      this.form.querySelector('.is-invalid')?.focus();
    }

    return isValid;
  }

  _buildCartItems() {
    const items = [];
    const addedCombinations = new Set(); // TASK-005: Track unique student+date combinations
    const registrationType = this.form.querySelector('input[name="registration_type"]:checked')?.value;
    const datesToAdd = [];

    // Determine which dates to add (only if date selection exists)
    if (this.options.hasDateSelection) {
      if (registrationType === 'all') {
        // Get all valid date options
        this.dateSelect?.querySelectorAll('option[value]:not([value=""])').forEach(opt => {
          datesToAdd.push({ value: opt.value, text: opt.text });
        });
        if (datesToAdd.length === 0) {
          this._showGeneralError('No available dates found to register for.');
          return null; // Indicate error
        }
      } else if (this.dateSelect?.value) {
        // Single date selection
        datesToAdd.push({ value: this.dateSelect.value, text: this.dateSelect.options[this.dateSelect.selectedIndex].text });
      }
    }

    // If no date selection section exists OR no dates were selected (for cases without date selection),
    // add one entry without a date to create base cart items
    if (datesToAdd.length === 0) {
      datesToAdd.push({ value: null, text: null });
    }

    // Get base properties (non-student, non-date specific)
    const baseProperties = {};
    const baseFields = Array.from(this.form.querySelectorAll('input[name^="properties["], select[name^="properties["]'))
      .filter(field => field !== this.dateSelect && !field.closest('.student-section') && !field.disabled);

    baseFields.forEach(field => {
      const propName = field.name.match(/properties\[(.*?)\]/)?.[1];
      if (propName) {
        baseProperties[propName] = field.value;
      }
    });

    // Determine if this is a kids class or adult class (TASK-006)
    const isKidsClass = this.options.collectKidsInfo;
    const isAdultClass = this.options.collectAdultInfo;

    // Iterate through each student and each date
    for (let i = 0; i < this.studentCount; i++) {
      const studentSection = this.studentsContainer.querySelector(`.student-section[data-student-index="${i}"]`);
      if (!studentSection) continue;

      const studentSpecificProps = {};
      studentSpecificProps['Student Number'] = `${i + 1}`;
      studentSpecificProps['First Name'] = studentSection.querySelector(`input[name="student-first-name-${i}"]`)?.value || '';
      studentSpecificProps['Last Name'] = studentSection.querySelector(`input[name="student-last-name-${i}"]`)?.value || '';

      // Only collect age for kids classes (TASK-006)
      if (isKidsClass) {
        const ageInput = studentSection.querySelector(`input[name="student-age-${i}"]`);
        if (ageInput) {
          studentSpecificProps['Age'] = ageInput.value || '';
        }
      }

      // Collect phone for adult classes
      if (isAdultClass) {
        const phoneInput = studentSection.querySelector(`input[name="student-phone-${i}"]`);
        if (phoneInput) {
          studentSpecificProps['Phone'] = phoneInput.value || '';
        }
      }

      // For each date, create a cart item combining base, student, and date properties
      datesToAdd.forEach(dateInfo => {
        // TASK-005: Create unique key for student + date combination
        const combinationKey = `${studentSpecificProps['First Name'].toLowerCase().trim()}-${studentSpecificProps['Last Name'].toLowerCase().trim()}-${dateInfo.value || 'no-date'}`;

        if (addedCombinations.has(combinationKey)) {
          console.warn(`Duplicate registration skipped: ${combinationKey}`);
          return; // Skip duplicate
        }

        addedCombinations.add(combinationKey);

        const itemProperties = { ...baseProperties, ...studentSpecificProps };
        if (dateInfo.value) {
          // Use the formatted text for display, value for consistency if needed elsewhere
          itemProperties['Class Date'] = dateInfo.text;
        }

        items.push({
          id: this.currentVariantId,
          quantity: 1,
          properties: itemProperties
        });
      });
    }

    if (items.length === 0 && this.studentCount > 0) {
      this._showGeneralError('Could not determine items to add. Please check selections.');
      return null;
    }

    return { items };
  }

  async _handleSubmit(event) {
    event.preventDefault();
    if (this.isSubmitting) return;

    // Check variant availability before validation
    if (!this.variantAvailable) {
      this._showGeneralError('This class is currently unavailable. Please select a different option.');
      return;
    }

    if (!this._validateForm()) {
      return;
    }

    this.isSubmitting = true;
    this._setLoadingState(true);
    this._clearAllErrors(); // Clear previous status messages

    try {
      const cartData = this._buildCartItems();

      if (!cartData || cartData.items.length === 0) {
        // Error message handled within _buildCartItems
        throw new Error("No items to add to cart."); // Prevent further processing
      }

      console.log('Submitting to cart:', JSON.stringify(cartData, null, 2)); // Debugging

      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(cartData)
      });

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          // Handle cases where response is not JSON
          errorData = { description: response.statusText || 'Unknown error occurred' };
        }
        console.error('Cart Add Error Response:', errorData);
        throw new Error(errorData.description || errorData.message || `Failed to add items (Status: ${response.status})`);
      }

      this._handleSuccess();

    } catch (error) {
      console.error('Submission Error:', error);
      this._handleError(error.message || 'An unexpected error occurred. Please try again.');
    } finally {
      this.isSubmitting = false;
      this._setLoadingState(false);
    }
  }

  _handleSuccess() {
    this._showGeneralSuccess('Successfully added to cart!');
    localStorage.removeItem(`productRegistrationForm_${this.options.productId}`); // Clear saved data for this product

    // Optional: Update cart icon/drawer immediately
    if (typeof window.refreshCart === 'function') {
      window.refreshCart();
    } else if (document.querySelector('cart-drawer')) {
      // Example for Dawn theme's cart drawer
      document.querySelector('cart-drawer').renderContents(new Event('render'));
    }

    // Redirect to cart page after a short delay
    setTimeout(() => {
      window.location.href = window.Shopify.routes.root + 'cart';
    }, 1500);
  }

  _handleError(message) {
    this._showGeneralError(message);
  }

  // --- UI Feedback Methods ---

  _setLoadingState(isLoading) {
    this.addToCartButton.disabled = isLoading;
    this.form.classList.toggle('is-submitting', isLoading);

    // Update ATC button text/content
    const buttonText = this.addToCartButton.querySelector('.button__text') || this.addToCartButton;
    if (isLoading) {
      buttonText.textContent = 'Adding...';
      this.addToCartButton.classList.add('loading');
    } else {
      buttonText.textContent = this.addToCartButton.dataset.originalText || 'Add to Cart';
      this.addToCartButton.classList.remove('loading');
    }
    // Store original text if not already done
    if (!this.addToCartButton.dataset.originalText) {
      this.addToCartButton.dataset.originalText = buttonText.textContent;
    }

    // Disable/enable other controls
    this.addStudentButton?.toggleAttribute('disabled', isLoading);
    this.removeStudentButton?.toggleAttribute('disabled', isLoading);
  }

  // TASK-008: Enhanced error handling with proper ARIA attributes
  _showFieldError(field, message) {
    const fieldId = field.id;
    const errorId = `error-${fieldId}`;
    const errorContainer = document.getElementById(errorId)
      || field.parentElement?.querySelector('.field-error-message');

    if (errorContainer) {
      errorContainer.textContent = message;
      errorContainer.setAttribute('role', 'alert');
      // Ensure error container has an ID for aria-describedby
      if (!errorContainer.id && fieldId) {
        errorContainer.id = errorId;
      }
    }

    field.setAttribute('aria-invalid', 'true');
    if (errorContainer?.id) {
      field.setAttribute('aria-describedby', errorContainer.id);
    }
  }

  _clearFieldError(field) {
    const errorContainer = field.parentElement?.querySelector('.field-error-message');
    if (errorContainer) {
      errorContainer.textContent = '';
    }
    field.removeAttribute('aria-invalid');
    field.removeAttribute('aria-describedby');
    field.classList.remove('is-invalid');
  }

  _clearAllErrors() {
    this.form.querySelectorAll('.field-error-message').forEach(el => el.textContent = '');
    this.form.querySelectorAll('.is-invalid').forEach(el => el.classList.remove('is-invalid'));
    this.form.querySelectorAll('[aria-invalid]').forEach(el => el.removeAttribute('aria-invalid'));
    if (this.generalErrorMessage) this.generalErrorMessage.textContent = '';
    if (this.generalSuccessMessage) this.generalSuccessMessage.textContent = '';
    this.formStatus?.classList.add('hidden');
  }

  _showGeneralError(message) {
    if (!this.formStatus || !this.generalErrorMessage) return;
    this.generalErrorMessage.textContent = message;
    if (this.generalSuccessMessage) this.generalSuccessMessage.textContent = '';
    this.formStatus.classList.remove('hidden');
    this.formStatus.classList.remove('status--success');
    this.formStatus.classList.add('status--error');
  }

  _showGeneralSuccess(message) {
    if (!this.formStatus || !this.generalSuccessMessage) return;
    this.generalSuccessMessage.textContent = message;
    if (this.generalErrorMessage) this.generalErrorMessage.textContent = '';
    this.formStatus.classList.remove('hidden');
    this.formStatus.classList.remove('status--error');
    this.formStatus.classList.add('status--success');
  }

  // --- Local Storage Methods ---

  _saveFormData() {
    try {
      const formData = new FormData(this.form);
      const data = Object.fromEntries(formData.entries());
      // Add student count and structure explicitly if needed
      data._studentCount = this.studentCount;
      // Add student data explicitly
      data._students = [];
      for (let i = 0; i < this.studentCount; i++) {
        const studentData = {};
        const studentSection = this.studentsContainer.querySelector(`.student-section[data-student-index="${i}"]`);
        studentSection?.querySelectorAll('input').forEach(input => {
          studentData[input.name] = input.value;
        });
        data._students.push(studentData);
      }

      localStorage.setItem(`productRegistrationForm_${this.options.productId}`, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving form data:', error);
    }
  }

  _restoreFormData() {
    try {
      const savedData = localStorage.getItem(`productRegistrationForm_${this.options.productId}`);
      if (!savedData) return;

      const data = JSON.parse(savedData);

      // Restore student count and sections first
      const savedStudentCount = parseInt(data._studentCount, 10) || 1;
      while (this.studentCount < savedStudentCount) {
        this._addStudent();
      }
      while (this.studentCount > savedStudentCount && this.studentCount > 1) {
        this._removeStudent();
      }

      // Restore general form fields
      Object.entries(data).forEach(([key, value]) => {
        if (key.startsWith('_')) return; // Skip internal keys like _studentCount, _students

        const field = this.form.querySelector(`[name="${key}"]`);
        if (field) {
          if (field.type === 'radio' || field.type === 'checkbox') {
            field.checked = field.value === value;
          } else {
            field.value = value;
          }
          // Trigger change event for dependent logic (like time slots)
          if (field.matches('select')) {
            this._handleInputChange(field);
          }
        }
      });

      // Restore dynamic student fields
      if (data._students && Array.isArray(data._students)) {
        data._students.forEach((studentData, index) => {
          const studentSection = this.studentsContainer.querySelector(`.student-section[data-student-index="${index}"]`);
          if (studentSection) {
            Object.entries(studentData).forEach(([name, value]) => {
              const input = studentSection.querySelector(`[name="${name}"]`);
              if (input) input.value = value;
            });
          }
        });
      }

      // Re-validate or update UI based on restored data
      this._populateTimeSlots();
      this._updateScheduleSummary();
      this._toggleDateSelectionVisibility();

    } catch (error) {
      console.error('Error restoring form data:', error);
      localStorage.removeItem(`productRegistrationForm_${this.options.productId}`);
    }
  }
}